import { ReactNode, useId } from 'react';
import { useFieldContext } from '@/lib/form.ts';
import { Select, SelectContent, SelectTrigger, SelectValue } from '@/common/components/ui/select';
import { Label } from '@/common/components/ui/label';
import { COMMON_VALIDATION_ERRORS, ZodIssueMap } from '@/common/form/utils.ts';
import { FormFieldErrors } from '@/common/components/form-field-errors/FormFieldErrors.tsx';
import { useShouldShowFieldErrors } from '@/common/hooks/useShouldShowFieldErrors.ts';
import { cn } from '@/lib/utils';

const zodIssueMap: ZodIssueMap = {
  invalidType: COMMON_VALIDATION_ERRORS.required,
  tooSmall: COMMON_VALIDATION_ERRORS.required,
};

export interface FormSelectFieldProps {
  label?: ReactNode;
  placeholder?: string;
  fullWidth?: boolean;
  disabled?: boolean;
  required?: boolean;
  errorMessage?: string;
  children: ReactNode;
}

export const FormSelectField = ({
  children,
  label,
  fullWidth = true,
  disabled,
  required,
  errorMessage,
  placeholder,
}: FormSelectFieldProps) => {
  const field = useFieldContext();
  const id = useId();

  const { shouldShowFieldErrors, onBlur } = useShouldShowFieldErrors(!!errorMessage);

  const handleBlur = () => {
    field.handleBlur();
    onBlur();
  };

  return (
    <div className={cn('flex flex-col gap-1.5', fullWidth && 'w-full')}>
      {label && (
        <Label htmlFor={id} className={cn(shouldShowFieldErrors && 'text-destructive')}>
          {label}
          {required && <span className="ml-0.5 text-destructive">*</span>}
        </Label>
      )}
      <Select
        value={field.state.value === null ? '' : (field.state.value as string)}
        onValueChange={(val) => field.handleChange(val === '' ? null : val)}
        disabled={disabled}
      >
        <SelectTrigger
          id={id}
          onBlur={handleBlur}
          className={cn(
            fullWidth && 'w-full',
            shouldShowFieldErrors && 'border-destructive focus:ring-destructive'
          )}
        >
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>{children}</SelectContent>
      </Select>
      {shouldShowFieldErrors && (
        <div className="text-sm text-destructive">
          <FormFieldErrors zodIssueMap={zodIssueMap} />
          {errorMessage && (
            <span data-testid={`${field.name}-error`} className="block">
              {errorMessage}
            </span>
          )}
        </div>
      )}
    </div>
  );
};
